import { RadioButtonGroup, Box } from '@sequencehq/core-components'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { StepTitle } from '../components/StepTitle'
import { match } from 'ts-pattern'
import { ExistingCustomerForm } from './forms/ExistingCustomerForm'
import { NewCustomerForm } from './forms/NewCustomerForm'
import {
  currentFormIsInvalidAtom,
  customerTypeAtom,
  showValidationErrorsAtom
} from './atoms/reviewCustomer.atoms'
import {
  CustomerType,
  isValidatingAtom,
  ReviewCustomerStepData
} from './atoms/reviewCustomer.primitives.atoms'
import { Step, StepComponentProps } from '../steps.types'
import { ScopeProvider } from 'jotai-scope'
import { StepLayout } from '../components/StepLayout'
import { StepFooter } from '../components/StepFooter'

const REVIEW_CUSTOMER_STEP_WIDTH = '522px'

export type ReviewCustomerStep = Step<'REVIEW_CUSTOMER', ReviewCustomerStepData>

export const BaseReviewCustomer = (props: StepComponentProps) => {
  const isValidating = useAtomValue(isValidatingAtom)
  const [customerType, setCustomerType] = useAtom(customerTypeAtom)
  const setShowValidationErrors = useSetAtom(showValidationErrorsAtom)
  const currentFormIsInvalid = useAtomValue(currentFormIsInvalidAtom)

  return (
    <StepLayout
      data-testid="reviewCustomer"
      title={<StepTitle>Customer</StepTitle>}
      footer={
        <StepFooter
          backDisabled
          nextDisabled={isValidating}
          onNext={async () => {
            if (currentFormIsInvalid) {
              setShowValidationErrors(true)
              return
            }
            await props.onNext()
          }}
          onBack={() => props.onBack()}
        />
      }
    >
      {/* Capped at the step width on desktop, but never wider than the phone
          screen it now has to fit. */}
      <Box
        marginBottom="24px"
        width={REVIEW_CUSTOMER_STEP_WIDTH}
        maxWidth="100%"
      >
        <RadioButtonGroup<CustomerType>
          fullWidth
          data-testid="customer.type"
          options={[
            {
              label: 'Existing Customer',
              value: 'existing'
            },
            {
              label: 'New Customer',
              value: 'new'
            }
          ]}
          onChange={value => {
            setShowValidationErrors(false)
            setCustomerType(value)
          }}
          value={customerType}
        />
      </Box>
      {match(customerType)
        .with('existing', () => <ExistingCustomerForm />)
        .with('new', () => <NewCustomerForm />)
        .exhaustive()}
    </StepLayout>
  )
}

export const ReviewCustomer = (props: StepComponentProps) => {
  return (
    <ScopeProvider atoms={[isValidatingAtom]}>
      <BaseReviewCustomer {...props} />
    </ScopeProvider>
  )
}
